import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { DaySelector } from "../components/DaySelector";
import { DetailPageSpotHeader } from "../components/DetailPageSpotHeader";
import { ErrorState } from "../components/ErrorState";
import { Footer } from "../components/Footer";
import { HourlyForecastCard } from "../components/HourlyForecastCard";
import { SpotMapCard } from "../components/map/SpotMapCard";
import { Navbar } from "../components/Navbar";
import { NearbySpots } from "../components/NearbySpots";
import { SpotDescription } from "../components/SpotDescription";
import { useForecast } from "../lib/api";
import { PointForecastError, useCustomLocationForecast } from "../lib/customLocation";
import { useUnit } from "../lib/hooks";
import { useScrollToTopOnMount } from "../lib/scrollRestoration";
import { STATIC_SPOTS } from "../lib/staticSpots";
import type { EnrichedSpot, ScoredHour, Spot } from "../lib/types";
import { useMeta } from "../lib/useMeta";
import { navbarHourLabel } from "../lib/utils";
import styles from "./DetailPage.module.css";

/** Either a curated spot id (`/spot/:id`) or a custom lat/lon pair
 *  (`/point/:lat/:lon`). The lat/lon variant fetches via
 *  `/api/point` so the wind numbers match what the user sees on the
 *  map (see docs/decisions.md → "One wind data pipeline for every
 *  location"). */
type DetailParams = { id: string } | { lat: string; lon: string };

interface DetailPageProps {
  params: DetailParams;
}

function isPointParams(p: DetailParams): p is { lat: string; lon: string } {
  return "lat" in p && "lon" in p;
}

/** Read `?day=N` from the current URL once on mount; safe default 0. */
function readInitialDay(): number {
  if (typeof window === "undefined") return 0;
  const raw = new URLSearchParams(window.location.search).get("day");
  if (!raw) return 0;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 && n < 10 ? n : 0;
}

interface FlatHour extends ScoredHour {
  dayOffset: number;
}

/** Flatten visible-day hours into one array (matches the bar chart's order).
 *  Includes "skip" days that still have hourly data (e.g. today's evening
 *  hours after the 08–19 daytime summary window has closed) so the chart
 *  bars and the day-strip labels stay in lockstep. */
function flattenHours(spot: EnrichedSpot, visibleDays: number): FlatHour[] {
  const out: FlatHour[] = [];
  for (const day of spot.days.slice(0, visibleDays)) {
    for (const h of day.hours) out.push({ ...h, dayOffset: day.dayOffset });
  }
  return out;
}

/** Button label for a "go back" action, reflecting the destination so
 *  the user knows where it lands them. */
function backLabel(target: string): string {
  if (target === "/") return "Back to regions";
  if (target.startsWith("/region/")) return "Back to spots";
  return "Back";
}

/** Default-selected hour for a given day: noon, or `Now` if today. */
function defaultSelIdx(allHours: FlatHour[], dayOffset: number): number {
  if (dayOffset === 0) {
    const i = allHours.findIndex((h) => h.isNow);
    if (i >= 0) return i;
  }
  const noon = allHours.findIndex(
    (h) => h.dayOffset === dayOffset && Number.parseInt(h.time.slice(11, 13), 10) === 12,
  );
  if (noon >= 0) return noon;
  const first = allHours.findIndex((h) => h.dayOffset === dayOffset);
  return first >= 0 ? first : 0;
}

/**
 * Loading-state skeleton for the detail page. Mirrors the real content
 * layout (header → day strip → hourly card → description + map → footer)
 * so the page doesn't reflow when the forecast lands. For a curated spot
 * the bundled `staticSpot` metadata fills the real header (area / name /
 * "Best in" tags) immediately — only the data-dependent regions shimmer.
 * Custom `/point` routes have no static metadata, so the header shimmers
 * too.
 */
function DetailSkeleton({ staticSpot }: { staticSpot: Spot | null }) {
  return (
    <div className={styles.content}>
      {staticSpot ? (
        <DetailPageSpotHeader
          area={staticSpot.area}
          name={staticSpot.name}
          idealDirs={staticSpot.ideal.dirs}
        />
      ) : (
        <div className={styles.skelHeader}>
          <span className={`${styles.skelBox} ${styles.skelArea}`} />
          <span className={`${styles.skelBox} ${styles.skelName}`} />
        </div>
      )}

      {/* Day strip already has a first-class skeleton mode (real dates,
          shimmering otherwise) — reuse it by passing no days. */}
      <DaySelector days={undefined} selectable />

      <div className={styles.skelHourlyCard}>
        <div className={styles.skelSummary}>
          <span className={`${styles.skelBox} ${styles.skelSpeed}`} />
          <span className={`${styles.skelBox} ${styles.skelDir}`} />
        </div>
        <span className={`${styles.skelBox} ${styles.skelChart}`} />
      </div>

      <div className={styles.descAndMap}>
        <div className={styles.skelDesc}>
          <span className={`${styles.skelBox} ${styles.skelLine}`} />
          <span className={`${styles.skelBox} ${styles.skelLine}`} />
          <span className={`${styles.skelBox} ${styles.skelLineShort}`} />
        </div>
        <span className={styles.skelMap} aria-hidden />
      </div>

      <Footer />
    </div>
  );
}

export function DetailPage({ params }: DetailPageProps) {
  const [location, setLocation] = useLocation();
  useScrollToTopOnMount(location);
  // Curated-spot path: pull from the bulk forecast.
  const forecast = useForecast();
  // Custom-point path: only enabled when the route carries lat/lon.
  // Parse once per render — the hook key handles re-fetching when they
  // change (e.g. navigating from one dropped pin to another).
  const pointCoords = isPointParams(params)
    ? {
        lat: Number.parseFloat(params.lat),
        lon: Number.parseFloat(params.lon),
      }
    : null;
  const pointValid =
    pointCoords !== null && Number.isFinite(pointCoords.lat) && Number.isFinite(pointCoords.lon);
  const pointQuery = useCustomLocationForecast(pointValid ? pointCoords : null);
  const { unit, cycleUnit } = useUnit();

  // Scroll-past-header → show spot name in the navbar. IntersectionObserver
  // fires when the spot header has scrolled out of view above the navbar
  // (rootMargin shrinks the viewport's top by --nav-bar-height so the
  // crossover happens exactly where the navbar's bottom edge sits).
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [showTitleInNavbar, setShowTitleInNavbar] = useState(false);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setShowTitleInNavbar(!entry?.isIntersecting),
      { rootMargin: "-88px 0px 0px 0px", threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
    // Re-observe whenever the route target changes (revisits a different
    // spot or point without unmounting DetailPage during a soft nav).
  }, [isPointParams(params) ? `${params.lat},${params.lon}` : params.id]);

  const spot = useMemo<EnrichedSpot | undefined>(() => {
    if (isPointParams(params)) return pointQuery.data;
    return forecast.data?.spots.find((s) => s.id === params.id);
  }, [params, forecast.data, pointQuery.data]);

  // Resolve spot metadata for <title> / <meta description> from the
  // static bundle so SEO tags are correct on first paint, before the
  // forecast lands. Custom /point/:lat/:lon routes don't get indexed
  // (see robots.txt) so a generic fallback is fine there.
  const staticSpot = useMemo(() => {
    if (isPointParams(params)) return null;
    return STATIC_SPOTS.find((s) => s.id === params.id) ?? null;
  }, [params]);
  useMeta(
    staticSpot
      ? {
          title: `${staticSpot.name} wind forecast — Big Wind`,
          description: `Hourly and 7-day wind forecast for ${staticSpot.name}, Sweden — for kiters, wingfoilers and windsurfers.`,
          path: `/spot/${staticSpot.id}`,
        }
      : { path: "/" },
  );

  // Loading / error state, merged across the two data sources so the
  // render guards below can stay route-agnostic.
  const isLoading = isPointParams(params) ? pointQuery.isLoading : forecast.isLoading;
  const isError = isPointParams(params) ? pointQuery.isError : forecast.isError;
  // A dropped pin whose /api/point call 400s is an unservable coordinate
  // (inland / outside coverage), not a transient failure — so we show a
  // "no forecast here" dead-end with a way back instead of a Retry.
  const outOfCoverage =
    pointQuery.error instanceof PointForecastError && pointQuery.error.status === 400;
  const refetch = () => {
    if (isPointParams(params)) pointQuery.refetch();
    else forecast.refetch();
  };

  // Back target. Curated spots return to their region overview. A custom
  // dropped pin doesn't belong to a region from this route's perspective —
  // the caller threads `?back=` so we can land back where the user came
  // from (e.g. the Stockholm overview where they long-pressed). Falls
  // back to the regions home if no hint is present.
  const backTarget = (() => {
    if (isPointParams(params)) {
      if (typeof window !== "undefined") {
        const back = new URLSearchParams(window.location.search).get("back");
        if (back) return back;
      }
      return "/";
    }
    return spot ? `/region/${spot.region}` : "/";
  })();

  // Selection state. selectedDay defaults from ?day=N; selectedHourIdx is null
  // until first user interaction (computed default until then).
  const [selectedDay, setSelectedDay] = useState<number>(readInitialDay);
  const [selectedHourIdx, setSelectedHourIdx] = useState<number | null>(null);

  // Force the chart to scroll-to-day on initial mount and on day-pill click.
  // The tick increments to retrigger the effect even when the day value
  // happens to be the same. (Page scroll-to-top is handled by
  // useScrollToTopOnMount above.)
  const [scrollTick, setScrollTick] = useState(0);
  useEffect(() => {
    if (selectedDay > 0) setScrollTick((t) => t + 1);
    // Only on first paint with a non-zero day; subsequent bumps are explicit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Always flatten ALL available days — HourlyChart is scrollable so there's
  // no reason to limit it to the viewport column count (visibleDays).
  const allHours = useMemo(() => (spot ? flattenHours(spot, spot.days.length) : []), [spot]);

  const resolvedHourIdx =
    selectedHourIdx !== null && selectedHourIdx < allHours.length
      ? selectedHourIdx
      : defaultSelIdx(allHours, selectedDay);

  const heroHour = allHours[resolvedHourIdx] ?? spot?.wind ?? null;

  const handleSelectHour = useCallback((idx: number, dayOffset: number) => {
    setSelectedHourIdx(idx);
    setSelectedDay(dayOffset);
  }, []);

  const handleSelectDay = useCallback(
    (dayOffset: number) => {
      setSelectedDay(dayOffset);
      setSelectedHourIdx(defaultSelIdx(allHours, dayOffset));
      setScrollTick((t) => t + 1);
    },
    [allHours],
  );

  const handleChartActiveDayChange = useCallback(
    (dayOffset: number) => {
      setSelectedDay((prev) => (prev === dayOffset ? prev : dayOffset));
      // When the chart scrolls into a new day, snap the selected hour to that
      // day's noon (or "now" for today) so the hero data matches what's on
      // screen instead of staying on a now-offscreen hour.
      setSelectedHourIdx((prev) => {
        const currentDay = prev !== null ? allHours[prev]?.dayOffset : null;
        if (currentDay === dayOffset) return prev;
        return defaultSelIdx(allHours, dayOffset);
      });
    },
    [allHours],
  );

  // ── Render guards ─────────────────────────────────────────────────────────
  // Error / dead-end pages drop the unit toggle — there's no forecast to
  // switch units on — and show just a back button.
  if (isError) {
    return (
      <div className={styles.detailLayout}>
        <div className={styles.navbarSticky}>
          <div className={styles.navbarInner}>
            <Navbar variant="back-only" onBack={() => setLocation(backTarget)} />
          </div>
        </div>
        <div className={styles.content}>
          {outOfCoverage ? (
            <ErrorState
              message="No forecast for that spot — it might be inland or outside our coverage."
              actionLabel={backLabel(backTarget)}
              onAction={() => setLocation(backTarget)}
            />
          ) : (
            <ErrorState
              message="Couldn't load the forecast — probably us, not you. Give it another shot."
              onAction={() => refetch()}
            />
          )}
        </div>
      </div>
    );
  }

  // Still loading — keep the full navbar (unit toggle and all) so nothing
  // shifts when the forecast lands and the real page renders.
  if (!spot && isLoading) {
    return (
      <div className={styles.detailLayout}>
        <div className={styles.navbarSticky}>
          <div className={styles.navbarInner}>
            <Navbar
              variant="detail"
              onBack={() => setLocation(backTarget)}
              unit={unit}
              onCycleUnit={cycleUnit}
            />
          </div>
        </div>
        <DetailSkeleton staticSpot={staticSpot} />
      </div>
    );
  }

  if (!spot) {
    return (
      <div className={styles.detailLayout}>
        <div className={styles.navbarSticky}>
          <div className={styles.navbarInner}>
            <Navbar variant="back-only" onBack={() => setLocation(backTarget)} />
          </div>
        </div>
        <div className={styles.content}>
          <ErrorState
            message="Can't find that spot. It might've been renamed or moved."
            actionLabel={backLabel(backTarget)}
            onAction={() => setLocation(backTarget)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.detailLayout}>
      <div className={styles.navbarSticky}>
        <div className={styles.navbarInner}>
          <Navbar
            variant="detail"
            onBack={() => setLocation(backTarget)}
            unit={unit}
            onCycleUnit={cycleUnit}
            {...(showTitleInNavbar
              ? {
                  title: spot.name,
                  ...(heroHour ? { subtitle: navbarHourLabel(heroHour.time) } : {}),
                }
              : {})}
          />
        </div>
      </div>

      <div className={styles.content}>
        <div ref={headerRef}>
          <DetailPageSpotHeader area={spot.area} name={spot.name} idealDirs={spot.ideal.dirs} />
        </div>

        <DaySelector
          days={spot.days}
          selectable
          selectedDay={selectedDay}
          onSelectDay={handleSelectDay}
        />

        <HourlyForecastCard
          spot={spot}
          visibleDays={spot.days.length}
          unit={unit}
          selectedHour={heroHour}
          selectedHourIdx={resolvedHourIdx}
          scrollToDayTick={scrollTick}
          scrollToDayOffset={selectedDay}
          onSelectHour={handleSelectHour}
          onActiveDayChange={handleChartActiveDayChange}
        />

        {/* Description + map: stacked on mobile (map above description via
            column-reverse) and side-by-side on desktop max-width per Figma
            223:12491. SpotDescription gets a fixed 540px, map fills the rest. */}
        <div className={styles.descAndMap}>
          <SpotDescription
            spotName={spot.name}
            note={spot.note}
            water={spot.water}
            access={spot.access}
          />

          <SpotMapCard spot={spot} heroHour={heroHour} />
        </div>

        {!isPointParams(params) && (
          <NearbySpots
            current={spot}
            spots={forecast.data?.spots ?? []}
            selectedDay={selectedDay}
            unit={unit}
          />
        )}

        <Footer />
      </div>
    </div>
  );
}
